/**
 * ADMIN LICENSES REACT QUERY HOOKS
 *
 * React Query hooks for managing licenses in super admin dashboard
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLicenseService, type LicenseFilters, type CreateLicenseRequest, type UpdateLicenseRequest } from '@/services/adminLicenseService';
import { useToast } from '@/hooks/use-toast';

// =====================================================
// QUERY HOOKS
// =====================================================

/**
 * Get all licenses with optional filtering
 */
export function useAdminLicenses(filters?: LicenseFilters) {
  return useQuery({
    queryKey: ['admin-licenses', filters],
    queryFn: () => AdminLicenseService.getAllLicenses(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Get single license by ID with full details
 */
export function useAdminLicense(id: string) {
  return useQuery({
    queryKey: ['admin-license', id],
    queryFn: () => AdminLicenseService.getLicenseById(id),
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Get license statistics
 */
export function useAdminLicenseStats() {
  return useQuery({
    queryKey: ['admin-license-stats'],
    queryFn: () => AdminLicenseService.getLicenseStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get license activations for a specific license
 */
export function useLicenseActivations(licenseId: string) {
  return useQuery({
    queryKey: ['license-activations', licenseId],
    queryFn: () => AdminLicenseService.getLicenseActivations(licenseId),
    enabled: !!licenseId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Get all companies for license assignment
 */
export function useAdminCompanies() {
  return useQuery({
    queryKey: ['admin-companies'],
    queryFn: () => AdminLicenseService.getAllCompanies(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// =====================================================
// MUTATION HOOKS
// =====================================================

/**
 * Create new license
 */
export function useCreateLicense() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (request: CreateLicenseRequest) =>
      AdminLicenseService.createLicense(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin-license-stats'] });
      toast({
        title: '✅ License Created',
        description: `License key: ${data.license_key}`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Creating License',
        description: error.message,
      });
    },
  });
}

/**
 * Update existing license
 */
export function useUpdateLicense(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (updates: UpdateLicenseRequest) =>
      AdminLicenseService.updateLicense(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin-license', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-license-stats'] });
      toast({
        title: '✅ License Updated',
        description: 'License has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Updating License',
        description: error.message,
      });
    },
  });
}

/**
 * Extend license expiration
 */
export function useExtendLicense(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (newExpiryDate: Date) =>
      AdminLicenseService.extendLicense(id, newExpiryDate),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin-license', id] });
      toast({
        title: '✅ License Extended',
        description: `New expiry date: ${new Date(data.expires_at).toLocaleDateString()}`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Extending License',
        description: error.message,
      });
    },
  });
}

/**
 * Suspend a license
 */
export function useSuspendLicense(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (reason: string) => AdminLicenseService.suspendLicense(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin-license', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-license-stats'] });
      toast({
        title: '⚠️ License Suspended',
        description: 'License has been suspended.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Suspending License',
        description: error.message,
      });
    },
  });
}

/**
 * Reactivate a suspended license
 */
export function useReactivateLicense(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => AdminLicenseService.reactivateLicense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin-license', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-license-stats'] });
      toast({
        title: '✅ License Reactivated',
        description: 'License has been reactivated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Reactivating License',
        description: error.message,
      });
    },
  });
}

/**
 * Revoke a license
 */
export function useRevokeLicense(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (reason: string) => AdminLicenseService.revokeLicense(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['admin-license', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-license-stats'] });
      toast({
        title: '🚫 License Revoked',
        description: 'License has been revoked and all devices deactivated.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Revoking License',
        description: error.message,
      });
    },
  });
}

/**
 * Deactivate a device from a license
 */
export function useDeactivateDevice(licenseId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (deviceFingerprint: string) =>
      AdminLicenseService.deactivateDevice(licenseId, deviceFingerprint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['license-activations', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['admin-license', licenseId] });
      toast({
        title: '✅ Device Deactivated',
        description: 'Device has been removed from this license.',
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Error Deactivating Device',
        description: error.message,
      });
    },
  });
}
